import React, { useEffect, useState } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { getFirebaseAuth, getFirebaseFunctions } from "@repo/backend/firebase";
import { BASE_PLAN, ADD_ON_MAP } from "@repo/backend/billing/tiers";
import type { ClientConfig } from "@repo/backend/schema/clientConfig";

interface AccountPageProps {
  config: ClientConfig;
}

type PageState =
  | "checking"
  | "login"
  | "unauthorized"
  | "dashboard"
  | "portal-loading"
  | "error";

function hasAccountAccess(
  claims: Record<string, unknown>,
  clientId: string
): boolean {
  if (claims["superAdmin"] === true) return true;
  const clients = claims["clients"] as Record<string, string> | undefined;
  return ["owner", "manager"].includes(clients?.[clientId] ?? "");
}

export function AccountPage({ config }: AccountPageProps) {
  const [state, setState] = useState<PageState>("checking");
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);

  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setState("login");
        return;
      }
      const tokenResult = await u.getIdTokenResult();
      if (hasAccountAccess(tokenResult.claims, config.clientId)) {
        setUser(u);
        setState("dashboard");
      } else {
        setState("unauthorized");
      }
    });
    return unsub;
  }, [config.clientId]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setAuthError(null);
    try {
      const auth = getFirebaseAuth();
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged fires and transitions state
    } catch {
      setAuthError("Invalid email or password.");
    }
  }

  async function handleManageBilling() {
    setState("portal-loading");
    setPortalError(null);
    try {
      const fns = getFirebaseFunctions();
      const createPortal = httpsCallable<
        { clientId: string; returnUrl: string },
        { url: string }
      >(fns, "createBillingPortalSession");
      const result = await createPortal({
        clientId: config.clientId,
        returnUrl: window.location.href,
      });
      window.location.href = result.data.url;
    } catch {
      setPortalError("Could not open billing portal. Please try again.");
      setState("dashboard");
    }
  }

  async function handleSignOut() {
    await signOut(getFirebaseAuth());
    setState("login");
    setUser(null);
  }

  const subStatus = config.subscription?.status;
  const subInterval = config.subscription?.interval ?? "monthly";
  const periodEnd = config.subscription?.currentPeriodEnd;
  const activeAddOns = (config.subscription?.addOns ?? []).map((id) => ADD_ON_MAP.get(id as never)).filter(Boolean);
  const isActive = subStatus === "active";

  // ── Loading ───────────────────────────────────────────────────────────────
  if (state === "checking" || state === "portal-loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">
          {state === "portal-loading" ? "Opening billing portal…" : "Loading…"}
        </div>
      </div>
    );
  }

  // ── Login ─────────────────────────────────────────────────────────────────
  if (state === "login") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {config.businessName}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Sign in to manage your subscription.
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-[var(--radius)] border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-[var(--radius)] border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            {authError && (
              <p className="text-sm text-destructive">{authError}</p>
            )}
            <button
              type="submit"
              className="w-full rounded-[var(--radius)] bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Sign in
            </button>
          </form>
          <a href="/" className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to site
          </a>
        </div>
      </div>
    );
  }

  // ── Unauthorized ──────────────────────────────────────────────────────────
  if (state === "unauthorized") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center space-y-4">
          <p className="text-foreground font-medium">
            You don't have access to this account.
          </p>
          <button
            onClick={handleSignOut}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background px-4 py-12">
      <div className="max-w-md mx-auto space-y-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {config.businessName}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {user?.email}
            </p>
          </div>
          <button
            onClick={handleSignOut}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Sign out
          </button>
        </div>

        {/* Subscription card */}
        <div className="rounded-[var(--radius)] border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-card-foreground">Subscription</h2>
            <span
              className={[
                "text-xs font-medium px-2 py-0.5 rounded-full",
                subStatus === "active"
                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                  : subStatus === "past_due"
                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                  : "bg-muted text-muted-foreground",
              ].join(" ")}
            >
              {subStatus ?? config.status}
            </span>
          </div>

          {isActive ? (
            <div className="space-y-1">
              <div className="text-lg font-bold text-card-foreground">Base Plan</div>
              <div className="text-sm text-muted-foreground">
                ${subInterval === "yearly"
                  ? `${Math.round(BASE_PLAN.yearlyPrice / 12)}/mo — billed $${BASE_PLAN.yearlyPrice}/yr`
                  : `${BASE_PLAN.monthlyPrice}/mo`}
              </div>
              {activeAddOns.length > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Add-ons</div>
                  {activeAddOns.map((a) => a && (
                    <div key={a.id} className="text-sm text-card-foreground flex justify-between">
                      <span>{a.label}</span>
                      <span className="text-muted-foreground">
                        +${subInterval === "yearly" ? Math.round(a.yearlyPrice / 12) : a.monthlyPrice}/mo
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              No active subscription yet.{" "}
              <a href="/#pricing" className="underline hover:text-foreground transition-colors">
                Choose a plan
              </a>
            </div>
          )}

          {periodEnd && (
            <div className="text-sm text-muted-foreground">
              Renews{" "}
              {new Date(periodEnd).toLocaleDateString(undefined, {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </div>
          )}

          {portalError && (
            <p className="text-sm text-destructive">{portalError}</p>
          )}

          {isActive && (
            <button
              onClick={handleManageBilling}
              className="w-full rounded-[var(--radius)] border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
            >
              Manage billing →
            </button>
          )}
        </div>

        <a
          href="/"
          className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to site
        </a>
      </div>
    </div>
  );
}
